import { skipToken } from '@reduxjs/toolkit/query';
import {
  getSpreadPctDisplay,
  isPricingModeRelative,
  type PricingModeEnum,
  resolvePricingModeDisplaySpecs,
  SideEnum,
  toBig,
  useSecuritiesContext,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import { useAppStateSelector } from '../../../providers/AppStateProvider';
import { baseRestApi } from '../../../utils/rtkQuery';
import { useManualRFQPricingContext } from './ManualRFQPricingContextProvider';
import { buildCustomerQuotePricingRequest } from './pricingRequest';

/**
 * A hook which connects to the manual rfq pricing context and its state, makes requests using RTKQ to
 * the /preview-price endpoint, and returns the latest revision of that preview. Will only return a preview object
 * if it should be rendered. As in, will return undefined if there are errors in the form, or if the pricing method
 * isn't per-leg.
 */
export const useCompositePricePreview = () => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableBottomUpPricingPreviews } = useFeatureFlag();
  const { selectors } = useManualRFQPricingContext();
  const state = useAppStateSelector(selectors.selectState);
  const anyErrors = useAppStateSelector(selectors.form.selectBidAsksError);
  const quote = state.currentCustomerQuote;

  // There is no reason to ask for a preview if the backend has already populated these fields for us.
  // We send a bottom-up price -> backend responds with a new CQ with both leg prices + composite prices -> we do _not_ need to re-request for a preview.
  const bidEligibleForPreview = quote?.Side !== SideEnum.Buy && !state.form.compositeBidAsk.bid.hasValue;
  const askEligibleForPreview = quote?.Side !== SideEnum.Sell && !state.form.compositeBidAsk.ask.hasValue;
  const inputsEligibleForPreview = bidEligibleForPreview || askEligibleForPreview;

  const disablePreview =
    !enableBottomUpPricingPreviews ||
    state.form.pricingMethod !== 'per-leg' ||
    !!anyErrors ||
    !inputsEligibleForPreview;

  const request: PreviewRequestBody | typeof skipToken = useMemo(() => {
    if (disablePreview || !quote) {
      return skipToken;
    }

    return {
      RFQID: quote.RFQID,
      ...buildCustomerQuotePricingRequest({ quote, formState: state.form }),
    };
  }, [disablePreview, quote, state.form]);

  const { data, isLoading, isFetching } = usePreviewPricingQuery(request);

  const previewToRender = useMemo(() => {
    const preview = data?.at(0);
    if (!preview || disablePreview) {
      return undefined;
    }

    let bidPxBig = toBig(preview.BidPx);
    let offerPxBig = toBig(preview.OfferPx);

    const spread =
      bidPxBig && offerPxBig
        ? getSpreadPctDisplay(bidPxBig, offerPxBig, { isRelativePrice: isPricingModeRelative(preview.PricingMode) })
        : undefined;

    const security = preview.Symbol ? securitiesBySymbol.get(preview.Symbol) : undefined;
    const { toDisplayPriceBig } = resolvePricingModeDisplaySpecs({
      pricingMode: preview.PricingMode,
      security,
    }).entryPrice;

    if (toDisplayPriceBig) {
      bidPxBig = toDisplayPriceBig(bidPxBig);
      offerPxBig = toDisplayPriceBig(offerPxBig);
    }

    const bid = bidPxBig?.toFixed();
    const offer = offerPxBig?.toFixed();

    return {
      bid,
      offer,
      bidDisplay: bid ? `~ ${bid}` : undefined,
      offerDisplay: offer ? `~ ${offer}` : undefined,
      spread,
    };
  }, [data, disablePreview, securitiesBySymbol]);

  return { preview: previewToRender, fetching: isLoading || isFetching };
};

interface PreviewRequestBody {
  RFQID: string;
  QuoteID: string;
  BidQty?: string;
  OfferQty?: string;
  LegPrices?: { Symbol: string; BidPx?: string; OfferPx?: string }[];
}

interface PreviewRequestResponse {
  Symbol?: string;
  BidPx: string;
  OfferPx: string;
  PricingMode: PricingModeEnum;
  Currency?: string;
}

const previewBottomUpPricingEndpoints = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    previewPricing: builder.query<PreviewRequestResponse[], PreviewRequestBody>({
      query: body => ({
        method: 'POST',
        url: `/customer/quotes/${body.RFQID}/preview-price`,
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

const { usePreviewPricingQuery } = previewBottomUpPricingEndpoints;
